import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  presentFactProvenance,
  type EvidenceClass,
} from '@/lib/passport/factProvenance';

const CLASS_STYLES: Record<EvidenceClass, string> = {
  assertion: 'bg-muted text-muted-foreground border-border',
  attestation: 'bg-success/10 text-success border-success/30',
  derivation: 'bg-primary/10 text-primary border-primary/30',
  // OD-35 (2026-10-15) gave measurement and external report approved copy.
  measurement: 'bg-secondary text-secondary-foreground border-border',
  external_report: 'bg-muted text-muted-foreground border-border',
  // Founder decision 2026-10-15: "Inferred by OptiBay" — styled apart from the derivation's "Calculated".
  inference: 'bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/40 border-dashed',
};

interface PassportProvenanceBadgeProps {
  /**
   * The fact group this badge qualifies, as registered in
   * `PASSPORT_FACT_PROVENANCE`.
   *
   * Deliberately NOT an evidence class. A component cannot hand this badge a
   * class, so it cannot decide one — the registry does, from the owning
   * capability's authority. An unregistered id renders nothing.
   */
  factGroupId: string;
  /**
   * The authoritative human-readable attributor, where the fact group carries
   * one per row (a school name, say). Ignored for classes that are not
   * attributed. An id-shaped value is refused rather than shown.
   */
  attributedTo?: unknown;
  className?: string;
}

/**
 * CORE-C GAP-002 — the one Passport provenance badge.
 *
 * It qualifies A FACT, not a card and not a person. Place it beside the thing
 * it describes: a card that mixes origins must not carry one badge across all
 * of it, because "Calculated by OptiBay" sitting over a group that also
 * contains a measured minute count says something false about the minutes.
 *
 * RENDERS NOTHING when the fact group is unregistered, or when its class has no
 * approved human copy. That silence is the safe state and is load-bearing:
 * SP-05 has six classes and inference has no badge wording (OD-35), so an
 * `inference` must produce no badge rather than borrow the nearest approved
 * phrase.
 *
 * It says nothing about how MUCH evidence there is. Sample counts, "Limited
 * evidence" and gating indicators are a different question — sufficiency, not
 * provenance — and they are left exactly where they are.
 */
export function PassportProvenanceBadge({
  factGroupId,
  attributedTo,
  className,
}: PassportProvenanceBadgeProps) {
  const presentation = presentFactProvenance(factGroupId, attributedTo);
  if (!presentation) return null;

  return (
    <Badge
      variant="outline"
      className={cn('text-xs font-normal', CLASS_STYLES[presentation.evidenceClass], className)}
      title={presentation.description}
      data-provenance-class={presentation.evidenceClass}
      data-provenance-fact={factGroupId}
    >
      {presentation.label}
    </Badge>
  );
}
